/**
 * Interpolation utilities for smoothing vehicle movement along roads
 */

// Interpolate points along a straight line with specified distance between points
export function interpolatePoints(start: { lat: number, lng: number }, end: { lat: number, lng: number }, stepDistance: number = 5): Array<{ lat: number, lng: number }> {
  // Calculate total distance
  const R = 6371000; // Earth radius in meters
  const lat1 = start.lat * Math.PI / 180;
  const lat2 = end.lat * Math.PI / 180;
  const dLat = (end.lat - start.lat) * Math.PI / 180;
  const dLon = (end.lng - start.lng) * Math.PI / 180;

  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1) * Math.cos(lat2) *
            Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c; // Distance in meters

  // If distance is small, don't interpolate
  if (distance <= stepDistance) {
    return [];
  }

  // Calculate number of points to interpolate
  const numPoints = Math.floor(distance / stepDistance);
  
  // Generate interpolated points
  const points: Array<{ lat: number, lng: number }> = [];
  for (let i = 1; i < numPoints; i++) {
    const fraction = i / numPoints;
    const lat = start.lat + fraction * (end.lat - start.lat);
    const lng = start.lng + fraction * (end.lng - start.lng);
    points.push({ lat, lng });
  }
  
  return points;
}

// Snap a point to the nearest road segment
export async function snapToRoad(points: Array<{ lat: number, lng: number }>): Promise<Array<{ lat: number, lng: number }>> {
  // This is a simplified version - in production you'd use a service like Google Roads API or OSRM
  // For now, we'll just return the original points
  return points;
}

// Generate a smooth path between two points that follows roads
export async function generateRoadAlignedPath(
  start: { lat: number, lng: number }, 
  end: { lat: number, lng: number }, 
  stepDistance: number = 100
): Promise<Array<{ lat: number, lng: number }>> {
  // First interpolate in straight line
  const interpolated = interpolatePoints(start, end, stepDistance);
  
  // If no interpolation needed, return empty array
  if (interpolated.length === 0) {
    return [];
  }
  
  // Add start and end points
  const allPoints = [start, ...interpolated, end];
  
  // Try to snap to road if available
  try {
    return await snapToRoad(allPoints);
  } catch (error) {
    console.warn('Road snapping failed, using linear interpolation', error);
    return interpolated;
  }
}
